import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { useQueryClient } from "@tanstack/react-query";
import { isApiError, unwrap } from "@brad-tipper/app-frontend";
import { api, sessionStore } from "~/api/client";
import type { Session, User } from "~/api/generated/types.gen";
import { pendingMfa } from "~/api/mfa-pending";
import { Platform } from "react-native";
import {
  backendConfigured,
  DEPLOYMENT_ENVIRONMENT,
  publicConfig,
} from "./constants";
import { migrateGuestDataOnSignup } from "./guest-data";
import { getAppUrl } from "~/utils/app-url";
import { usePurchases } from "~/hooks/usePurchases";

export type AuthUser = {
  id: string;
  email: string;
  name: string;
  emailVerified: boolean;
  mfaEnabled: boolean;
};

type AuthValue = {
  user: AuthUser | null;
  ready: boolean;
  configured: boolean;
  error: string;
  /** Whether the signed-in user has an active DoughBro Pro entitlement.
   * Guests and free accounts are always false -- see useStarter/
   * useRecipeProgress/useRecipeTimer, which gate server sync on this. */
  hasPro: boolean;
  purchases: ReturnType<typeof usePurchases>;
  convertToEmailPassword(
    email: string,
    password: string,
    name: string,
    captchaToken?: string,
  ): Promise<{ error?: string }>;
  signInWithEmailPassword(
    email: string,
    password: string,
    captchaToken?: string,
  ): Promise<{ error?: string }>;
  resetPassword(
    email: string,
    captchaToken?: string,
  ): Promise<{ error?: string; message?: string }>;
  /** Finishes "forgot password" with the one-time token from the emailed link. */
  finishPasswordReset(
    token: string,
    password: string,
  ): Promise<{ error?: string }>;
  updateProfile(displayName: string): Promise<{ error?: string }>;
  /** Completes a sign-in that stopped at the authenticator-code step. */
  completeMfaSignIn(input: {
    code?: string;
    recoveryCode?: string;
  }): Promise<void>;
  signOut(): Promise<void>;
  refresh(): Promise<void>;
};

const AuthContext = createContext<AuthValue | null>(null);

const USER_CACHE_KEY = "doughbro.user";

/**
 * Browser tests cannot buy a subscription. A spec can opt in to Pro-only server
 * sync by setting `localStorage["e2e:forcePro"] = "1"` before the app loads.
 * It only changes what the client does (nothing is granted server-side) and is
 * ignored on any deployed host.
 */
function e2eForcePro(): boolean {
  if (DEPLOYMENT_ENVIRONMENT !== "local" || Platform.OS !== "web") return false;
  try {
    return globalThis.localStorage?.getItem("e2e:forcePro") === "1";
  } catch {
    return false;
  }
}

function normalizeUser(user: User): AuthUser {
  return {
    id: user.id,
    email: user.email,
    name: user.name || user.email.split("@")[0] || "Guest Baker",
    emailVerified: user.emailConfirmed,
    mfaEnabled: user.mfaEnabled,
  };
}

async function cacheUser(user: AuthUser | null) {
  try {
    if (user) await AsyncStorage.setItem(USER_CACHE_KEY, JSON.stringify(user));
    else await AsyncStorage.removeItem(USER_CACHE_KEY);
  } catch {
    // The cache only speeds up an offline start.
  }
}

async function readCachedUser(): Promise<AuthUser | null> {
  try {
    const raw = await AsyncStorage.getItem(USER_CACHE_KEY);
    return raw ? (JSON.parse(raw) as AuthUser) : null;
  } catch {
    return null;
  }
}

function LocalAuthProvider({ children }: { children: ReactNode }) {
  const backendRequired = publicConfig.deploymentEnvironment === "production";
  const purchases = usePurchases(null);
  const value = useMemo<AuthValue>(
    () => ({
      user: null,
      ready: true,
      configured: false,
      hasPro: false,
      purchases,
      error: backendRequired
        ? "Something isn’t set up properly on our end, so accounts are off for now."
        : "",
      async convertToEmailPassword() {
        return { error: "Saving an account isn’t available right now." };
      },
      async signInWithEmailPassword() {
        return { error: "Sign-in isn’t available right now." };
      },
      async resetPassword() {
        return { error: "Password reset isn’t available right now." };
      },
      async finishPasswordReset() {
        return { error: "Password reset isn’t available right now." };
      },
      async updateProfile() {
        return { error: "Profile updates aren’t available right now." };
      },
      async completeMfaSignIn() {},
      async signOut() {},
      async refresh() {},
    }),
    [backendRequired, purchases],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Matches the API: 12+ characters with lower, upper, digit and symbol. */
export function passwordProblem(password: string): string | null {
  if (
    password.length < 12 ||
    !/[a-z]/.test(password) ||
    !/[A-Z]/.test(password) ||
    !/\d/.test(password) ||
    !/[^A-Za-z0-9]/.test(password)
  ) {
    return "Use at least 12 characters with a lowercase letter, an uppercase letter, a number and a symbol.";
  }
  return null;
}

function ensureEmailPassword(
  email: string,
  password: string,
):
  { ok: true; email: string; password: string } | { ok: false; error: string } {
  const normalizedEmail = email.trim().toLowerCase();
  if (!normalizedEmail.includes("@")) {
    return { ok: false, error: "Please put in a real email address." };
  }
  const problem = passwordProblem(password);
  if (problem) return { ok: false, error: problem };
  return { ok: true, email: normalizedEmail, password };
}

function messageOf(error: unknown, fallback: string) {
  if (isApiError(error)) {
    if (error.code === "invalid_credentials") {
      return "That email and password don’t match.";
    }
    return error.message || fallback;
  }
  return error instanceof Error && error.message ? error.message : fallback;
}

function ApiAuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<AuthUser | null>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState("");
  const purchases = usePurchases(user?.id ?? null);

  const adoptUser = useCallback((next: AuthUser | null) => {
    setUser(next);
    void cacheUser(next);
  }, []);

  /** Persists a new token pair and adopts its user. */
  const adopt = useCallback(
    async (session: Session) => {
      await sessionStore.set({
        accessToken: session.accessToken,
        refreshToken: session.refreshToken,
        expiresAt: session.expiresAt,
      });
      setError("");
      adoptUser(normalizeUser(session.user));
    },
    [adoptUser],
  );

  const refresh = useCallback(async () => {
    if (!sessionStore.get()) return;
    adoptUser(normalizeUser(await unwrap(api.getMe())));
  }, [adoptUser]);

  useEffect(() => {
    let active = true;

    void (async () => {
      // No auto sign-in: a visitor is a guest (starter/timers/progress
      // local-only) until they actually sign up or sign in. Nothing reaches
      // the database before that.
      const stored = await sessionStore.load();
      if (!stored) {
        if (active) setReady(true);
        return;
      }
      // Render from the cached user straight away, then confirm with the server.
      const cached = await readCachedUser();
      if (active && cached) setUser(cached);
      try {
        await refresh();
      } catch (loadError) {
        if (!active) return;
        if (isApiError(loadError) && loadError.status === 401) {
          await sessionStore.clear();
        } else if (!cached) {
          setError(messageOf(loadError, "Unable to load your account."));
        }
      } finally {
        if (active) setReady(true);
      }
    })();

    // A rejected refresh token clears the session: that is a sign-out.
    const unsubscribe = sessionStore.subscribe((stored) => {
      if (!active || stored) return;
      setUser(null);
      void cacheUser(null);
    });

    return () => {
      active = false;
      unsubscribe();
    };
  }, [refresh]);

  const value = useMemo<AuthValue>(
    () => ({
      user,
      ready,
      configured: true,
      hasPro: purchases.hasPro || e2eForcePro(),
      purchases,
      error,
      async convertToEmailPassword(email, password, name, captchaToken) {
        const checked = ensureEmailPassword(email, password);
        if (!checked.ok) return { error: checked.error };
        const trimmedName = name.trim() || "Guest Baker";
        let session: Session | null | undefined;
        try {
          const result = await unwrap(
            api.signUp({
              body: {
                email: checked.email,
                password: checked.password,
                name: trimmedName,
                redirectUrl: getAppUrl("/"),
                captchaToken,
              },
            }),
          );
          session = result.session;
        } catch (signUpError) {
          return {
            error: messageOf(signUpError, "Could not create the account."),
          };
        }
        if (!session) {
          return { error: "Check your email to finish creating your account." };
        }
        // The session must be stored before the guest data is copied across,
        // because the migration calls the API as the new account.
        await sessionStore.set({
          accessToken: session.accessToken,
          refreshToken: session.refreshToken,
          expiresAt: session.expiresAt,
        });
        try {
          await migrateGuestDataOnSignup(queryClient);
        } catch (migrationError) {
          await adopt(session);
          return {
            error:
              migrationError instanceof Error
                ? migrationError.message
                : "Account created, but we could not bring your guest data across.",
          };
        }
        // Adopting the user lets the starter/progress/timer queries fire; force
        // a refetch now that migration has committed so the just-migrated data
        // shows up immediately instead of waiting out staleTime.
        await adopt(session);
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ["starter"] }),
          queryClient.invalidateQueries({ queryKey: ["starter-logs"] }),
          queryClient.invalidateQueries({ queryKey: ["recipe-progress"] }),
          queryClient.invalidateQueries({ queryKey: ["recipe-timers"] }),
        ]);
        return {};
      },
      async signInWithEmailPassword(email, password, captchaToken) {
        const checked = ensureEmailPassword(email, password);
        if (!checked.ok) return { error: checked.error };
        try {
          const result = await unwrap(
            api.signIn({
              body: {
                email: checked.email,
                password: checked.password,
                captchaToken,
              },
            }),
          );
          if (result.mfaRequired && result.mfaToken) {
            // MfaGate takes over until the authenticator code is entered.
            pendingMfa.set(result.mfaToken);
            return {};
          }
          if (result.session) await adopt(result.session);
          return {};
        } catch (signInError) {
          return { error: messageOf(signInError, "Could not sign you in.") };
        }
      },
      async resetPassword(email, captchaToken) {
        const normalizedEmail = email.trim().toLowerCase();
        if (!normalizedEmail.includes("@")) {
          return { error: "Please put in a real email address." };
        }
        try {
          await unwrap(
            api.forgotPassword({
              body: {
                email: normalizedEmail,
                redirectUrl: getAppUrl("/reset-password"),
                captchaToken,
              },
            }),
          );
          return { message: "Check your email for a reset link." };
        } catch (resetError) {
          return {
            error: messageOf(resetError, "Could not send the reset email."),
          };
        }
      },
      async finishPasswordReset(token, password) {
        const problem = passwordProblem(password);
        if (problem) return { error: problem };
        try {
          await unwrap(api.resetPassword({ body: { token, password } }));
          return {};
        } catch (resetError) {
          return {
            error: messageOf(
              resetError,
              "This reset link is invalid or has expired.",
            ),
          };
        }
      },
      async updateProfile(displayName) {
        const name = displayName.trim();
        if (!name) return { error: "Name cannot be empty." };
        try {
          adoptUser(
            normalizeUser(await unwrap(api.updateMe({ body: { name } }))),
          );
          return {};
        } catch (updateError) {
          return { error: messageOf(updateError, "Could not save your name.") };
        }
      },
      async completeMfaSignIn(input) {
        const mfaToken = pendingMfa.get();
        if (!mfaToken) throw new Error("Sign in again to continue.");
        try {
          const result = await unwrap(
            api.verifyMfa({
              body: {
                mfaToken,
                code: input.code?.trim(),
                recoveryCode: input.recoveryCode?.trim(),
              },
            }),
          );
          if (result.session) await adopt(result.session);
          pendingMfa.set(null);
        } catch (mfaError) {
          throw new Error(messageOf(mfaError, "That code did not work."));
        }
      },
      async signOut() {
        try {
          if (sessionStore.get()) await api.signOut();
        } catch {
          // Signing out locally is what matters.
        }
        pendingMfa.set(null);
        await sessionStore.clear();
        adoptUser(null);
      },
      refresh,
    }),
    [adopt, adoptUser, error, purchases, queryClient, ready, refresh, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  if (!backendConfigured) {
    return <LocalAuthProvider>{children}</LocalAuthProvider>;
  }
  return <ApiAuthProvider>{children}</ApiAuthProvider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error("AuthProvider missing");
  return value;
}
